/**
 * Chiffrement de la sauvegarde Hybride (« coffre » envoyé au serveur Nexxus).
 *
 * Schéma (uniquement WebCrypto : AES-256-GCM, PBKDF2-SHA256, HKDF-SHA256, SHA-256) :
 *  - une clé de données (DEK) AES-256 aléatoire chiffre le contenu (IV 96 bits unique par coffre) ;
 *  - la DEK est enveloppée DEUX fois : par une clé dérivée de la phrase secrète (PBKDF2 ≥ 600 000
 *    itérations, sel aléatoire) et par une clé dérivée de la clé de récupération de 256 bits (HKDF) ;
 *    l'une OU l'autre suffit pour rouvrir le coffre ;
 *  - l'en-tête (format, version, kdf, métadonnées) est lié au contenu par AAD.
 * Le serveur ne reçoit jamais de clair ni de secret : uniquement l'enveloppe JSON ci-dessous.
 * Ce module est pur : aucune E/S, aucun accès au stockage ni au réseau.
 */

export const HYBRID_ENVELOPE_FORMAT = 'nexxus-hybrid-backup';
export const HYBRID_ENVELOPE_VERSION = 1;
export const PBKDF2_MIN_ITERATIONS = 600_000;
export const PASSPHRASE_MIN_LENGTH = 12;
const RECOVERY_KEY_BYTES = 32;
const RECOVERY_CHECKSUM_BYTES = 3;
const RECOVERY_INFO = 'nexxus-hybrid-backup/recovery/v1';

export type HybridCryptoErrorCode =
  | 'UNSUPPORTED_FORMAT'
  | 'KDF_TOO_WEAK'
  | 'PASSPHRASE_TOO_SHORT'
  | 'RECOVERY_KEY_INVALID'
  | 'WRONG_SECRET'
  | 'AUTH_FAILED'
  | 'BAD_INPUT';

export class HybridCryptoError extends Error {
  constructor(
    readonly code: HybridCryptoErrorCode,
    message: string,
  ) {
    super(message);
    this.name = 'HybridCryptoError';
  }
}

export interface HybridKdfParams {
  passphrase: { alg: 'PBKDF2-SHA256'; iterations: number; salt: string };
  recovery: { alg: 'HKDF-SHA256'; salt: string };
}

export interface HybridWrap {
  type: 'passphrase' | 'recovery';
  alg: 'AES-256-GCM';
  iv: string;
  wrapped_dek: string;
}

/** Éléments stockables localement pour fabriquer des coffres sans ressaisie (aucun secret en clair). */
export interface HybridKeyWraps {
  kdf: HybridKdfParams;
  wraps: HybridWrap[];
}

export interface HybridEnvelope extends HybridKeyWraps {
  format: typeof HYBRID_ENVELOPE_FORMAT;
  version: typeof HYBRID_ENVELOPE_VERSION;
  created_at: string;
  app_version: string;
  schema_version: number;
  payload: { alg: 'AES-256-GCM'; iv: string; ciphertext: string };
}

export interface HybridEnvelopeMeta {
  created_at: string;
  app_version: string;
  schema_version: number;
}

// --------------------------------------------------------------------------- utilitaires

const enc = new TextEncoder();
const dec = new TextDecoder();

function toBufferSource(bytes: Uint8Array): BufferSource {
  return new Uint8Array(bytes) as unknown as BufferSource;
}

export function bytesToB64(bytes: Uint8Array): string {
  let binary = '';
  for (let i = 0; i < bytes.length; i += 1) binary += String.fromCharCode(bytes[i]!);
  return btoa(binary);
}

export function b64ToBytes(value: unknown): Uint8Array {
  if (typeof value !== 'string' || !/^[A-Za-z0-9+/]*={0,2}$/.test(value)) {
    throw new HybridCryptoError('UNSUPPORTED_FORMAT', 'Enveloppe illisible (base64 invalide).');
  }
  const binary = atob(value);
  const out = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) out[i] = binary.charCodeAt(i);
  return out;
}

function randomBytes(n: number): Uint8Array {
  return crypto.getRandomValues(new Uint8Array(n));
}

function canonical(value: unknown): string {
  if (Array.isArray(value)) return `[${value.map(canonical).join(',')}]`;
  if (value && typeof value === 'object') {
    const rec = value as Record<string, unknown>;
    return `{${Object.keys(rec)
      .sort()
      .map((k) => `${JSON.stringify(k)}:${canonical(rec[k])}`)
      .join(',')}}`;
  }
  return JSON.stringify(value);
}

async function sha256Bytes(data: Uint8Array): Promise<Uint8Array> {
  return new Uint8Array(await crypto.subtle.digest('SHA-256', toBufferSource(data)));
}

// --------------------------------------------------------------------------- clé de récupération

const B32 = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ234567';

function base32Encode(bytes: Uint8Array): string {
  let bits = 0;
  let value = 0;
  let out = '';
  for (const b of bytes) {
    value = (value << 8) | b;
    bits += 8;
    while (bits >= 5) {
      out += B32[(value >>> (bits - 5)) & 31];
      bits -= 5;
    }
  }
  if (bits > 0) out += B32[(value << (5 - bits)) & 31];
  return out;
}

function base32Decode(text: string): Uint8Array | null {
  let bits = 0;
  let value = 0;
  const out: number[] = [];
  for (const ch of text) {
    const idx = B32.indexOf(ch);
    if (idx < 0) return null;
    value = (value << 5) | idx;
    bits += 5;
    if (bits >= 8) {
      out.push((value >>> (bits - 8)) & 0xff);
      bits -= 8;
    }
  }
  return Uint8Array.from(out);
}

async function recoveryChecksum(key: Uint8Array): Promise<Uint8Array> {
  const input = new Uint8Array([...enc.encode('nexxus-hybrid-recovery-v1'), ...key]);
  return (await sha256Bytes(input)).slice(0, RECOVERY_CHECKSUM_BYTES);
}

/** Clé de récupération brute : 256 bits aléatoires. */
export function generateRecoveryKey(): Uint8Array {
  return randomBytes(RECOVERY_KEY_BYTES);
}

/** Clé de données (DEK) AES-256 aléatoire. */
export function generateDek(): Uint8Array {
  return randomBytes(32);
}

/** Texte à imprimer : 14 groupes de 4 caractères base32 (256 bits + somme de contrôle de 24 bits). */
export async function formatRecoveryKey(key: Uint8Array): Promise<string> {
  if (key.length !== RECOVERY_KEY_BYTES) {
    throw new HybridCryptoError('RECOVERY_KEY_INVALID', 'Clé de récupération de longueur inattendue.');
  }
  const full = new Uint8Array([...key, ...(await recoveryChecksum(key))]);
  return base32Encode(full).match(/.{1,4}/g)!.join('-');
}

/** Relit la clé saisie (casse, espaces et tirets ignorés) et vérifie la somme de contrôle. */
export async function parseRecoveryKey(text: string): Promise<Uint8Array> {
  const bad = (): never => {
    throw new HybridCryptoError(
      'RECOVERY_KEY_INVALID',
      'Clé de récupération invalide : vérifiez la saisie (faute de frappe possible).',
    );
  };
  if (typeof text !== 'string') return bad();
  const clean = text.toUpperCase().replace(/[\s-]/g, '');
  if (clean.length !== 56) return bad();
  const bytes = base32Decode(clean);
  if (!bytes || bytes.length < RECOVERY_KEY_BYTES + RECOVERY_CHECKSUM_BYTES) return bad();
  const key = bytes.slice(0, RECOVERY_KEY_BYTES);
  const sum = bytes.slice(RECOVERY_KEY_BYTES, RECOVERY_KEY_BYTES + RECOVERY_CHECKSUM_BYTES);
  const expected = await recoveryChecksum(key);
  let diff = 0;
  for (let i = 0; i < sum.length; i += 1) diff |= sum[i]! ^ expected[i]!;
  if (diff !== 0) return bad();
  return key;
}

// --------------------------------------------------------------------------- dérivation / enveloppes de clé

function assertPassphrase(passphrase: string): void {
  if (typeof passphrase !== 'string' || [...passphrase].length < PASSPHRASE_MIN_LENGTH) {
    throw new HybridCryptoError(
      'PASSPHRASE_TOO_SHORT',
      `La phrase secrète doit contenir au moins ${String(PASSPHRASE_MIN_LENGTH)} caractères.`,
    );
  }
}

function assertIterations(iterations: unknown): asserts iterations is number {
  if (typeof iterations !== 'number' || !Number.isInteger(iterations) || iterations < PBKDF2_MIN_ITERATIONS) {
    throw new HybridCryptoError('KDF_TOO_WEAK', 'Paramètres de dérivation trop faibles.');
  }
}

async function derivePassphraseKey(passphrase: string, salt: Uint8Array, iterations: number): Promise<CryptoKey> {
  const base = await crypto.subtle.importKey('raw', enc.encode(passphrase.normalize('NFC')), 'PBKDF2', false, [
    'deriveKey',
  ]);
  return crypto.subtle.deriveKey(
    { name: 'PBKDF2', salt: toBufferSource(salt), iterations, hash: 'SHA-256' },
    base,
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt', 'decrypt'],
  );
}

async function deriveRecoveryKey(recovery: Uint8Array, salt: Uint8Array): Promise<CryptoKey> {
  const base = await crypto.subtle.importKey('raw', toBufferSource(recovery), 'HKDF', false, ['deriveKey']);
  return crypto.subtle.deriveKey(
    { name: 'HKDF', hash: 'SHA-256', salt: toBufferSource(salt), info: enc.encode(RECOVERY_INFO) },
    base,
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt', 'decrypt'],
  );
}

function wrapAad(type: HybridWrap['type'], kdf: HybridKdfParams): Uint8Array {
  return enc.encode(
    canonical({
      format: HYBRID_ENVELOPE_FORMAT,
      version: HYBRID_ENVELOPE_VERSION,
      type,
      kdf: kdf[type],
    }),
  );
}

async function wrapDek(
  type: HybridWrap['type'],
  key: CryptoKey,
  dek: Uint8Array,
  kdf: HybridKdfParams,
): Promise<HybridWrap> {
  const iv = randomBytes(12);
  const wrapped = await crypto.subtle.encrypt(
    { name: 'AES-GCM', iv: toBufferSource(iv), additionalData: toBufferSource(wrapAad(type, kdf)) },
    key,
    toBufferSource(dek),
  );
  return { type, alg: 'AES-256-GCM', iv: bytesToB64(iv), wrapped_dek: bytesToB64(new Uint8Array(wrapped)) };
}

function assertDek(dek: Uint8Array): void {
  if (!(dek instanceof Uint8Array) || dek.length !== 32) {
    throw new HybridCryptoError('BAD_INPUT', 'Clé de données invalide.');
  }
}

export interface BuildKeyWrapsInput {
  dek: Uint8Array;
  passphrase: string;
  recoveryKey: Uint8Array;
  /** Réservé aux évolutions : doit rester ≥ 600 000. */
  pbkdf2Iterations?: number;
}

/** Enveloppe la DEK par la phrase secrète ET par la clé de récupération. */
export async function buildKeyWraps(input: BuildKeyWrapsInput): Promise<HybridKeyWraps> {
  assertDek(input.dek);
  assertPassphrase(input.passphrase);
  const iterations = input.pbkdf2Iterations ?? PBKDF2_MIN_ITERATIONS;
  assertIterations(iterations);
  if (input.recoveryKey.length !== RECOVERY_KEY_BYTES) {
    throw new HybridCryptoError('RECOVERY_KEY_INVALID', 'Clé de récupération de longueur inattendue.');
  }
  const kdf: HybridKdfParams = {
    passphrase: { alg: 'PBKDF2-SHA256', iterations, salt: bytesToB64(randomBytes(16)) },
    recovery: { alg: 'HKDF-SHA256', salt: bytesToB64(randomBytes(32)) },
  };
  const pKey = await derivePassphraseKey(input.passphrase, b64ToBytes(kdf.passphrase.salt), iterations);
  const rKey = await deriveRecoveryKey(input.recoveryKey, b64ToBytes(kdf.recovery.salt));
  return {
    kdf,
    wraps: [
      await wrapDek('passphrase', pKey, input.dek, kdf),
      await wrapDek('recovery', rKey, input.dek, kdf),
    ],
  };
}

/** Change la phrase secrète (la DEK locale suffit) ; la clé de récupération reste valable. */
export async function changePassphraseWraps(
  current: HybridKeyWraps,
  input: { dek: Uint8Array; newPassphrase: string; pbkdf2Iterations?: number },
): Promise<HybridKeyWraps> {
  assertDek(input.dek);
  assertPassphrase(input.newPassphrase);
  const iterations = input.pbkdf2Iterations ?? PBKDF2_MIN_ITERATIONS;
  assertIterations(iterations);
  const kdf: HybridKdfParams = {
    passphrase: { alg: 'PBKDF2-SHA256', iterations, salt: bytesToB64(randomBytes(16)) },
    recovery: current.kdf.recovery,
  };
  const key = await derivePassphraseKey(input.newPassphrase, b64ToBytes(kdf.passphrase.salt), iterations);
  const recoveryWrap = current.wraps.find((w) => w.type === 'recovery');
  if (!recoveryWrap) throw new HybridCryptoError('BAD_INPUT', 'Enveloppe de récupération absente.');
  // L'AAD de l'enveloppe « recovery » ne dépend que de kdf.recovery : elle reste valide.
  return { kdf, wraps: [await wrapDek('passphrase', key, input.dek, kdf), recoveryWrap] };
}

/** Remplace la clé de récupération (ex. perdue ou exposée) ; la phrase secrète reste valable. */
export async function rotateRecoveryKeyWraps(
  current: HybridKeyWraps,
  input: { dek: Uint8Array; newRecoveryKey: Uint8Array },
): Promise<HybridKeyWraps> {
  assertDek(input.dek);
  if (input.newRecoveryKey.length !== RECOVERY_KEY_BYTES) {
    throw new HybridCryptoError('RECOVERY_KEY_INVALID', 'Clé de récupération de longueur inattendue.');
  }
  const kdf: HybridKdfParams = {
    passphrase: current.kdf.passphrase,
    recovery: { alg: 'HKDF-SHA256', salt: bytesToB64(randomBytes(32)) },
  };
  const key = await deriveRecoveryKey(input.newRecoveryKey, b64ToBytes(kdf.recovery.salt));
  const passphraseWrap = current.wraps.find((w) => w.type === 'passphrase');
  if (!passphraseWrap) throw new HybridCryptoError('BAD_INPUT', 'Enveloppe de phrase secrète absente.');
  return { kdf, wraps: [passphraseWrap, await wrapDek('recovery', key, input.dek, kdf)] };
}

// --------------------------------------------------------------------------- enveloppe

function payloadAad(h: Pick<HybridEnvelope, 'created_at' | 'app_version' | 'schema_version' | 'kdf'>): Uint8Array {
  return enc.encode(
    canonical({
      format: HYBRID_ENVELOPE_FORMAT,
      version: HYBRID_ENVELOPE_VERSION,
      created_at: h.created_at,
      app_version: h.app_version,
      schema_version: h.schema_version,
      kdf: h.kdf,
      payload_alg: 'AES-256-GCM',
    }),
  );
}

export interface CreateEnvelopeOptions {
  dek: Uint8Array;
  meta: HybridEnvelopeMeta;
  /** Enveloppes de clé déjà fabriquées (cas des sauvegardes automatiques). */
  wrapping?: HybridKeyWraps;
  /** Ou secrets bruts (premier coffre / coffre de sécurité). */
  passphrase?: string;
  recoveryKey?: Uint8Array;
}

/** Chiffre `plain` (texte) dans une enveloppe v1. IV 96 bits neuf à chaque appel. */
export async function createEnvelope(plain: string, opts: CreateEnvelopeOptions): Promise<HybridEnvelope> {
  assertDek(opts.dek);
  let wrapping = opts.wrapping;
  if (!wrapping) {
    if (opts.passphrase === undefined || !opts.recoveryKey) {
      throw new HybridCryptoError('BAD_INPUT', 'Phrase secrète et clé de récupération requises.');
    }
    wrapping = await buildKeyWraps({ dek: opts.dek, passphrase: opts.passphrase, recoveryKey: opts.recoveryKey });
  }
  assertIterations(wrapping.kdf.passphrase.iterations);
  const header = {
    created_at: opts.meta.created_at,
    app_version: opts.meta.app_version,
    schema_version: opts.meta.schema_version,
    kdf: wrapping.kdf,
  };
  const iv = randomBytes(12);
  const key = await crypto.subtle.importKey('raw', toBufferSource(opts.dek), 'AES-GCM', false, ['encrypt']);
  const ciphertext = await crypto.subtle.encrypt(
    { name: 'AES-GCM', iv: toBufferSource(iv), additionalData: toBufferSource(payloadAad(header)) },
    key,
    enc.encode(plain),
  );
  return {
    format: HYBRID_ENVELOPE_FORMAT,
    version: HYBRID_ENVELOPE_VERSION,
    created_at: header.created_at,
    app_version: header.app_version,
    schema_version: header.schema_version,
    kdf: wrapping.kdf,
    wraps: wrapping.wraps,
    payload: { alg: 'AES-256-GCM', iv: bytesToB64(iv), ciphertext: bytesToB64(new Uint8Array(ciphertext)) },
  };
}

export type HybridUnlockSecret = { passphrase: string } | { recoveryKey: string };

export interface OpenedEnvelope {
  plaintext: string;
  dek: Uint8Array;
  unlockedWith: 'passphrase' | 'recovery';
}

function assertEnvelopeShape(env: unknown): asserts env is HybridEnvelope {
  const e = env as Partial<HybridEnvelope> | null;
  if (
    !e ||
    typeof e !== 'object' ||
    e.format !== HYBRID_ENVELOPE_FORMAT ||
    e.version !== HYBRID_ENVELOPE_VERSION ||
    typeof e.created_at !== 'string' ||
    typeof e.app_version !== 'string' ||
    typeof e.schema_version !== 'number' ||
    !e.kdf?.passphrase ||
    !e.kdf.recovery ||
    !Array.isArray(e.wraps) ||
    !e.payload ||
    e.payload.alg !== 'AES-256-GCM'
  ) {
    throw new HybridCryptoError('UNSUPPORTED_FORMAT', 'Format de sauvegarde non reconnu.');
  }
}

/** Rouvre une enveloppe avec la phrase secrète OU la clé de récupération. */
export async function openEnvelope(envelope: HybridEnvelope, secret: HybridUnlockSecret): Promise<OpenedEnvelope> {
  assertEnvelopeShape(envelope);
  const usePassphrase = 'passphrase' in secret;
  const type: HybridWrap['type'] = usePassphrase ? 'passphrase' : 'recovery';
  const wrap = envelope.wraps.find((w) => w.type === type);
  if (!wrap) throw new HybridCryptoError('UNSUPPORTED_FORMAT', 'Ce coffre ne contient pas ce type de clé.');

  let wrapKey: CryptoKey;
  if (usePassphrase) {
    assertIterations(envelope.kdf.passphrase.iterations);
    wrapKey = await derivePassphraseKey(
      secret.passphrase,
      b64ToBytes(envelope.kdf.passphrase.salt),
      envelope.kdf.passphrase.iterations,
    );
  } else {
    const raw = await parseRecoveryKey(secret.recoveryKey);
    wrapKey = await deriveRecoveryKey(raw, b64ToBytes(envelope.kdf.recovery.salt));
  }

  let dek: Uint8Array;
  try {
    dek = new Uint8Array(
      await crypto.subtle.decrypt(
        {
          name: 'AES-GCM',
          iv: toBufferSource(b64ToBytes(wrap.iv)),
          additionalData: toBufferSource(wrapAad(type, envelope.kdf)),
        },
        wrapKey,
        toBufferSource(b64ToBytes(wrap.wrapped_dek)),
      ),
    );
  } catch (err) {
    if (err instanceof HybridCryptoError) throw err;
    throw new HybridCryptoError('WRONG_SECRET', 'Phrase secrète ou clé de récupération incorrecte.');
  }

  try {
    const key = await crypto.subtle.importKey('raw', toBufferSource(dek), 'AES-GCM', false, ['decrypt']);
    const plain = await crypto.subtle.decrypt(
      {
        name: 'AES-GCM',
        iv: toBufferSource(b64ToBytes(envelope.payload.iv)),
        additionalData: toBufferSource(payloadAad(envelope)),
      },
      key,
      toBufferSource(b64ToBytes(envelope.payload.ciphertext)),
    );
    return { plaintext: dec.decode(plain), dek, unlockedWith: type === 'passphrase' ? 'passphrase' : 'recovery' };
  } catch (err) {
    if (err instanceof HybridCryptoError) throw err;
    throw new HybridCryptoError('AUTH_FAILED', 'Sauvegarde altérée ou corrompue (authentification échouée).');
  }
}
